import { useCurrentStep } from "../../../hooks/track/useCurrentStep";
import { useClientTranslations } from "../../../lib/i18n/client";
import styles from "./CurrentStepCard.module.css";

/**
 * CurrentStepCard (CSC-01, CSC-03).
 *
 * Renders the patient's current cycle step (thumbnail + title + subtext)
 * from the Plan 36-04 hook. Title/subtext are looked up via i18n keys
 * `currentStep.{id}.title` / `currentStep.{id}.subtext` with a fallback to
 * the API's English copy (D-04).
 *
 * Failure model is non-blocking: a /api/current-step failure does NOT trip
 * the page-level error state (TRACK-03 only triggers on the events fetch).
 * On `isLoading` the card renders a skeleton block; on `isError` it renders
 * nothing (silent fallback).
 */
export function CurrentStepCard() {
	const { t } = useClientTranslations("track");
	const { data, isLoading, isError } = useCurrentStep();

	if (isLoading) {
		return <div className={styles.skeleton} aria-busy="true" />;
	}
	if (isError || !data) {
		return null;
	}

	const i18nTitleKey = `currentStep.${data.id}.title`;
	const i18nSubtextKey = `currentStep.${data.id}.subtext`;
	const titleResolved = t(i18nTitleKey, { defaultValue: data.title });
	const subtextResolved = t(i18nSubtextKey, { defaultValue: data.subtext });

	return (
		<article className={styles.card}>
			<img
				src={data.thumbnailUrl}
				alt=""
				className={styles.thumbnail}
				loading="lazy"
			/>
			<div className={styles.body}>
				<h3 className={styles.title}>{titleResolved}</h3>
				<p className={styles.subtext}>{subtextResolved}</p>
			</div>
		</article>
	);
}
